import { Plus } from 'lucide-react-native';
import { useMemo, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Badge, FormInput, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';

const SEGMENT_TAG_PREFIX = 'seg:';

function isSegmentTag(tag: string): boolean {
  return tag.startsWith(SEGMENT_TAG_PREFIX);
}

interface LeadTagsEditorProps {
  tags: string[];
  onChange: (next: string[]) => void;
  suggestions?: string[];
  isSaving?: boolean;
}

export function LeadTagsEditor({
  tags,
  onChange,
  suggestions = [],
  isSaving = false,
}: Readonly<LeadTagsEditorProps>) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');

  const manualTags = useMemo(() => tags.filter((tag) => !isSegmentTag(tag)), [tags]);
  const autoTags = useMemo(() => tags.filter(isSegmentTag), [tags]);

  const normalizedDraft = draft.trim();

  const filteredSuggestions = useMemo(() => {
    if (!normalizedDraft) return [];
    const query = normalizedDraft.toLowerCase();
    const applied = new Set(tags.map((tag) => tag.toLowerCase()));
    return suggestions
      .filter((suggestion) => !isSegmentTag(suggestion))
      .filter((suggestion) => suggestion.toLowerCase().includes(query))
      .filter((suggestion) => !applied.has(suggestion.toLowerCase()))
      .slice(0, 6);
  }, [normalizedDraft, suggestions, tags]);

  const addTag = (raw: string) => {
    if (isSaving) return;
    const value = raw.trim();
    if (!value || isSegmentTag(value)) return;
    const exists = tags.some((tag) => tag.toLowerCase() === value.toLowerCase());
    if (exists) return;
    onChange([...tags, value]);
    setDraft('');
  };

  const removeTag = (tag: string) => {
    if (isSaving || isSegmentTag(tag)) return;
    onChange(tags.filter((existing) => existing !== tag));
  };

  return (
    <VStack spacing="sm" testID="lead_tags_editor">
      <Text variant="caption" color={colors.text.tertiary} style={{ fontSize: 10 }}>
        {t('leads.detail.tags')}
      </Text>

      {(manualTags.length > 0 || autoTags.length > 0) && (
        <HStack spacing="xs" style={styles.tagRow}>
          {manualTags.map((tag) => (
            <Badge
              key={tag}
              testID={`lead_tag_${tag}`}
              variant="subtle"
              color="cyan"
              size="sm"
              onRemove={() => removeTag(tag)}
              removeTestID={`lead_tag_remove_${tag}`}
              removeAccessibilityLabel={t('leads.detail.tagsEditor.removeLabel', { tag })}
            >
              {tag}
            </Badge>
          ))}
          {autoTags.map((tag) => (
            <Badge
              key={tag}
              testID={`lead_autotag_${tag}`}
              variant="subtle"
              color="purple"
              size="sm"
              accessibilityLabel={t('leads.detail.tagsEditor.autoTagLabel', { tag })}
            >
              {tag}
            </Badge>
          ))}
        </HStack>
      )}

      <HStack spacing="xs" align="center">
        <View style={{ flex: 1 }}>
          <FormInput
            testID="lead_tag_input"
            value={draft}
            onChangeText={setDraft}
            onSubmitEditing={() => addTag(draft)}
            editable={!isSaving}
            placeholder={t('leads.detail.tagsEditor.placeholder')}
            placeholderTextColor={colors.text.tertiary}
            accessibilityLabel={t('leads.detail.tagsEditor.inputLabel')}
            returnKeyType="done"
            autoCapitalize="none"
          />
        </View>
        <Pressable
          testID="lead_tag_add"
          onPress={() => addTag(draft)}
          disabled={isSaving}
          hitSlop={6}
          style={({ pressed }) => [
            styles.addButton,
            pressed && styles.addButtonPressed,
            isSaving && styles.addButtonDisabled,
          ]}
          accessibilityRole="button"
          accessibilityState={{ disabled: isSaving }}
          accessibilityLabel={t('leads.detail.tagsEditor.addLabel')}
        >
          <Plus size={16} color={colors.primary[500]} strokeWidth={2} />
        </Pressable>
      </HStack>

      {filteredSuggestions.length > 0 && (
        <HStack spacing="xs" style={styles.tagRow}>
          {filteredSuggestions.map((suggestion) => (
            <Pressable
              key={suggestion}
              testID={`lead_tag_suggestion_${suggestion}`}
              onPress={() => addTag(suggestion)}
              disabled={isSaving}
              accessibilityRole="button"
              accessibilityLabel={t('leads.detail.tagsEditor.suggestionLabel', { tag: suggestion })}
            >
              <Badge variant="outline" color="neutral" size="sm">
                {suggestion}
              </Badge>
            </Pressable>
          ))}
        </HStack>
      )}
    </VStack>
  );
}

const styles = StyleSheet.create({
  tagRow: {
    flexWrap: 'wrap',
    rowGap: tokens.spacing.xs,
  },
  addButton: {
    width: 36,
    height: 36,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.default,
    backgroundColor: colors.surface.row,
    justifyContent: 'center',
    alignItems: 'center',
  },
  addButtonPressed: {
    opacity: 0.6,
  },
  addButtonDisabled: {
    opacity: 0.4,
  },
});
